import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LoadingOutlined } from "@ant-design/icons";
import { Spin } from "antd";
import { toast, Toaster } from "react-hot-toast";
import Pagination from "components/pagination";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { FiEye, FiPlus, FiSearch } from "react-icons/fi";
import { apiCall } from "utils/apiClient";

const antIcon = <LoadingOutlined style={{ fontSize: 56 }} spin />;

const normalizeServices = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.services)) return payload.services;
  return [];
};

const ServiceProcessesList = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [services, setServices] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(10);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      try {
        const servicesResult = await apiCall({ endpoint: "services", method: "GET" });

        if (servicesResult?.status === 200) {
          setServices(normalizeServices(servicesResult?.data));
        } else {
          toast.error(servicesResult?.message || "Failed to fetch services");
        }
      } catch (error) {
        console.error("Error fetching service processes data:", error);
        toast.error("Failed to load service processes");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const filteredServices = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return services;

    return services.filter((service) => {
      const serviceName = String(service?.name ?? service?.service_name ?? "").toLowerCase();

      return serviceName.includes(query);
    });
  }, [services, searchTerm]);

  const totalRecords = filteredServices.length;
  const totalPages = Math.max(1, Math.ceil(totalRecords / recordsPerPage));

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const startIndex = (currentPage - 1) * recordsPerPage;
  const endIndex = startIndex + recordsPerPage;
  const paginatedServices = filteredServices.slice(startIndex, endIndex);
  const displayedCount = totalRecords === 0 ? 0 : Math.min(totalRecords, endIndex);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Toaster position="top-center" reverseOrder={false} />

      {loading ? (
        <div className="flex h-[75vh] w-full items-center justify-center">
          <Spin indicator={antIcon} />
        </div>
      ) : (
        <div className="mt-5 space-y-4">
          <div className="rounded-xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
            <div>
              <div>
                <h2 className="text-[15px] font-semibold text-slate-800">Service Processes</h2>
                <p className="mt-1 text-xs text-slate-500">
                  View or add step-by-step process details for each service.
                </p>
              </div>
            </div>

            <div className="mt-4 grid gap-3 md:grid-cols-[minmax(0,1fr)_auto] md:items-center">
              <div className="relative">
                <FiSearch
                  size={15}
                  className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                />
                <input
                  type="text"
                  className="block w-full rounded-lg border border-slate-300 bg-slate-50 pl-9 pr-3 py-2 text-sm text-slate-900 focus:border-blue-500 focus:ring-blue-500"
                  placeholder="Search service by name"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                />
              </div>
              <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-center">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                  Total Services
                </p>
                <p className="mt-1 text-sm font-semibold text-slate-800">{totalRecords}</p>
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
            <div className="mx-4 mb-3 pt-4 rounded-lg border border-slate-100">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              recordsPerPage={recordsPerPage}
              onRecordsPerPageChange={(val) => {
                setRecordsPerPage(val);
                setCurrentPage(1);
              }}
              totalRecords={totalRecords}
              count={displayedCount}
              showFirstLastControls
              compact
            />
          </div>

          <div className="mx-4 mb-4 overflow-x-auto">
            <table className="w-full text-left text-xs text-slate-600">
              <thead className="bg-slate-50 text-[11px] uppercase text-slate-700">
                <tr>
                  <th className="px-3 py-2">Sr No</th>
                  <th className="px-3 py-2">Service Name</th>
                  <th className="px-3 py-2">Action</th>
                </tr>
              </thead>
              <tbody>
                {paginatedServices.length > 0 ? (
                  paginatedServices.map((service, index) => {
                    const serviceId = service?.id ?? service?.service_id;

                    return (
                      <tr
                        key={serviceId ?? index}
                        className="border-b border-slate-100 bg-white hover:bg-slate-50"
                      >
                        <td className="whitespace-nowrap px-3 py-2.5">
                          <span className="inline-flex min-w-[28px] items-center justify-center rounded-full bg-slate-100 px-2 py-1 text-[11px] font-semibold text-slate-600">
                            {startIndex + index + 1}
                          </span>
                        </td>
                        <td className="px-3 py-2.5">
                          <p className="font-medium text-slate-800">
                            {service?.name || service?.service_name || "-"}
                          </p>
                        </td>
                        <td className="px-3 py-2.5">
                          <div className="flex flex-wrap items-center gap-2">
                            <button
                              type="button"
                              onClick={() =>
                                navigate(`/service-processes/view/${serviceId}`, {
                                  state: { service },
                                })
                              }
                              className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 px-3 py-1.5 text-[11px] font-medium text-slate-700 hover:bg-slate-50"
                            >
                              <FiEye size={13} />
                              View
                            </button>
                            <button
                              type="button"
                              onClick={() =>
                                navigate(`/service-processes/create/${serviceId}`, {
                                  state: { service },
                                })
                              }
                              className="inline-flex items-center gap-1.5 rounded-md bg-indigo-600 px-3 py-1.5 text-[11px] font-medium text-white hover:bg-indigo-700"
                            >
                              <FiPlus size={13} />
                              Add Process
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={3} className="py-8 text-center text-sm text-slate-500">
                      No services found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="mx-4 mb-4 rounded-lg border border-slate-100">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              recordsPerPage={recordsPerPage}
              onRecordsPerPageChange={(val) => {
                setRecordsPerPage(val);
                setCurrentPage(1);
              }}
              totalRecords={totalRecords}
              count={displayedCount}
              showFirstLastControls
              compact
            />
          </div>
        </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default ServiceProcessesList;
